import i18n, { type InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { lazyLocaleBackend } from './lazyLocaleBackend';

/**
 * Onda 8 / Fatia 8.2 — lazy-load do i18n-resources.
 *
 * Até esta fatia, os 3 locales (pt-BR/en-US/es-ES) × 22 namespaces eram
 * importados estaticamente aqui (66 imports), todos indo pro bundle inicial
 * (~403KB gzip no chunk `i18n-resources`, herdado da Onda 6) mesmo que o
 * usuário só use um idioma. Agora:
 *
 *  - pt-BR (fallback/default — `fallbackLng` abaixo, e o idioma inicial de
 *    quem nunca trocou de idioma, ver `detection` abaixo) continua EAGER
 *    (import estático, os imports logo abaixo). Garante ZERO risco de flash de
 *    chave crua ou de atraso no 1º paint, e mantém síncronos os testes que
 *    importam `@/locales` diretamente (`ErrorBoundary.test.tsx`).
 *  - en-US/es-ES são buscados SOB DEMANDA por `lazyLocaleBackend` (backend
 *    customizado do i18next via `import.meta.glob`, zero dependência nova)
 *    — só quando o idioma detectado (`localStorage`) ou escolhido pelo
 *    usuário (`LanguageToggle`) for um desses dois.
 *
 * `partialBundledLanguages: true` é o que permite essa coexistência:
 * `resources` (abaixo, só pt-BR) vira um cache PARCIAL — o i18next ainda
 * consulta o backend para qualquer (idioma, namespace) que não esteja lá.
 * Sem essa opção, a presença de QUALQUER `resources` faz o i18next pular o
 * backend por completo. Não faz parte de `InitOptions` desta versão do
 * i18next (existe e funciona em runtime — ver dist/cjs/i18next.js —, só não
 * está tipado), daí o merge de tipo abaixo em vez de um `as any` genérico.
 *
 * `react-i18next` re-renderiza componentes assinantes (`useTranslation`)
 * quando o backend resolve, mesmo com `useSuspense: false` (inalterado) —
 * o usuário com en-US/es-ES ativo vê o fallback pt-BR por uma fração de
 * segundo até o locale correto chegar, igual ao comportamento já existente
 * para uma chave pontual não traduzida. Trade-off documentado em
 * spec/crm-onda8-followups-quickwins/spec.md §8.
 */

// pt-BR translations — eager (fallback/default, ver doc acima).
import ptBRCommon from './pt-BR/common.json';
import ptBRNavigation from './pt-BR/navigation.json';
import ptBRAuth from './pt-BR/auth.json';
import ptBRAgents from './pt-BR/agents.json';
import ptBRChat from './pt-BR/chat.json';
import ptBRSettings from './pt-BR/settings.json';
import ptBRMarketplace from './pt-BR/marketplace.json';
import ptBRBilling from './pt-BR/billing.json';
import ptBRErrors from './pt-BR/errors.json';
import ptBRWorkflows from './pt-BR/workflows.json';
import ptBRKnowledge from './pt-BR/knowledge.json';
import ptBRTools from './pt-BR/tools.json';
import ptBRAdmin from './pt-BR/admin.json';
import ptBRCrm from './pt-BR/crm.json';
import ptBRCrmAgentMissions from './pt-BR/crmAgentMissions.json';
import ptBRCrmVoiceAgents from './pt-BR/crmVoiceAgents.json';
import ptBRCrmLgpd from './pt-BR/crmLgpd.json';
import ptBRCrmPermissions from './pt-BR/crmPermissions.json';
import ptBRCrmAudit from './pt-BR/crmAudit.json';
import ptBRCrmWebhooks from './pt-BR/crmWebhooks.json';
import ptBRCrmContracts from './pt-BR/crmContracts.json';
import ptBRCrmSequences from './pt-BR/crmSequences.json';
import ptBRCrmShell from './pt-BR/crmShell.json';
import ptBRCrmDash from './pt-BR/crmDash.json';
import ptBRCrmComms from './pt-BR/crmComms.json';
import ptBRCrmAi from './pt-BR/crmAi.json';
import ptBRCrmTelephony from './pt-BR/crmTelephony.json';
import ptBRCrmSalesData from './pt-BR/crmSalesData.json';
import ptBRCrmOps from './pt-BR/crmOps.json';
import ptBRCrmAccount from './pt-BR/crmAccount.json';
import ptBRCrmAdmin from './pt-BR/crmAdmin.json';
import ptBRCrmLanding from './pt-BR/crmLanding.json';
import ptBRUsage from './pt-BR/usage.json';

export const defaultNS = 'common';

export const namespaces = [
  'common',
  'navigation',
  'auth',
  'agents',
  'chat',
  'settings',
  'marketplace',
  'billing',
  'errors',
  'workflows',
  'knowledge',
  'tools',
  // Namespace `admin` — MESMO nome e MESMAS chaves (`tools.templateSelector.*`)
  // do namespace `admin` dos apps irmaos (admin-layouts/principal e
  // cliente-layouts/principal). Componentes compartilhados que chamam
  // `useTranslation('admin')` — hoje o ToolTemplateSelector, candidato a
  // migrar para @oprus/web-core — resolvem aqui sem adaptacao. Nao confundir
  // com `crmAdmin`, que e a area de administracao DO CRM.
  'admin',
  'crm',
  'usage',
  // Onda 4 / Fatia 4.2 — agent-missions (pages/app/agent-missions/*) e
  // voice-agents (VoiceAgents.tsx), migrados sem useTranslation antes.
  'crmAgentMissions',
  'crmVoiceAgents',
  // === T0 (plumbing de i18n das ondas de melhorias do app) ===
  //
  // Namespace por fatia: cada fatia de trabalho paralelo é dona EXCLUSIVA dos 3 arquivos
  // (pt-BR/en-US/es-ES) do seu namespace. Duas vantagens concretas:
  //
  //  1. Zero colisão de escrita. Agentes rodando em paralelo nunca editam o
  //     mesmo JSON, então não há merge conflict nem risco de um sobrescrever
  //     as chaves do outro — cada um só toca `<seuNamespace>.json`.
  //  2. Os limiares de `src/__tests__/locales/i18n-parity.test.ts` continuam
  //     válidos. Aqueles percentuais foram CALIBRADOS contra a composição
  //     medida de cada namespace; despejar chaves novas dentro de `crm` ou
  //     `common` moveria a taxa de clone medida e faria o teste disparar (ou,
  //     pior, mascararia uma regressão real). Namespace novo = medição nova,
  //     isolada, sem tocar nos limiares já calibrados.
  //
  // Todos nascem VAZIOS (`{}`) nos 3 locales: o plumbing (registro aqui +
  // arquivo em cada locale) vem antes, e a onda dona de cada namespace o
  // preenche. Namespace vazio é inerte — o i18next simplesmente não resolve
  // chave nenhuma nele e cai no fallback, sem erro.
  //
  // A divisão é por ÁREA do app (lgpd, permissões, auditoria, …), não por
  // arquivo de origem: a forma lógica da chave continua sendo a da tela.
  'crmLgpd',
  'crmPermissions',
  'crmAudit',
  'crmWebhooks',
  'crmContracts',
  'crmSequences',
  'crmShell',
  'crmDash',
  'crmComms',
  'crmAi',
  'crmTelephony',
  'crmSalesData',
  'crmOps',
  'crmAccount',
  'crmAdmin',
  // Story 13 do redesign — a landing publica (`/`) tem copy de marketing que
  // nao cabe em nenhum namespace de tela. Fica fora de `crm` tambem para nao
  // mover as taxas ja calibradas de `i18n-parity.test.ts`.
  'crmLanding',
] as const;

/** Só pt-BR — en-US/es-ES vêm do `lazyLocaleBackend` (ver doc do módulo). */
export const resources = {
  'pt-BR': {
    common: ptBRCommon,
    navigation: ptBRNavigation,
    auth: ptBRAuth,
    agents: ptBRAgents,
    chat: ptBRChat,
    settings: ptBRSettings,
    marketplace: ptBRMarketplace,
    billing: ptBRBilling,
    errors: ptBRErrors,
    workflows: ptBRWorkflows,
    knowledge: ptBRKnowledge,
    tools: ptBRTools,
    admin: ptBRAdmin,
    crm: ptBRCrm,
    usage: ptBRUsage,
    crmAgentMissions: ptBRCrmAgentMissions,
    crmVoiceAgents: ptBRCrmVoiceAgents,
    crmLgpd: ptBRCrmLgpd,
    crmPermissions: ptBRCrmPermissions,
    crmAudit: ptBRCrmAudit,
    crmWebhooks: ptBRCrmWebhooks,
    crmContracts: ptBRCrmContracts,
    crmSequences: ptBRCrmSequences,
    crmShell: ptBRCrmShell,
    crmDash: ptBRCrmDash,
    crmComms: ptBRCrmComms,
    crmAi: ptBRCrmAi,
    crmTelephony: ptBRCrmTelephony,
    crmSalesData: ptBRCrmSalesData,
    crmOps: ptBRCrmOps,
    crmAccount: ptBRCrmAccount,
    crmAdmin: ptBRCrmAdmin,
    crmLanding: ptBRCrmLanding,
  },
} as const;

/**
 * `partialBundledLanguages` existe e funciona em runtime nesta versão do
 * i18next mas não está em `InitOptions` (ver doc do módulo) — merge de tipo
 * local em vez de `as any`.
 */
type InitOptionsWithPartialBundled = InitOptions & { partialBundledLanguages?: boolean };

const initOptions: InitOptionsWithPartialBundled = {
  resources,
  partialBundledLanguages: true,
  fallbackLng: 'pt-BR',
  defaultNS,
  ns: [...namespaces],
  detection: {
    // Apenas localStorage: sem nada salvo, cai no fallbackLng (pt-BR) em vez
    // do idioma do navegador. Garante pt-BR como padrão; a escolha do usuário
    // no seletor de idioma é persistida aqui e respeitada nas próximas visitas.
    order: ['localStorage'],
    lookupLocalStorage: 'language-storage',
    caches: ['localStorage'],
  },
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  react: {
    useSuspense: false, // Disable suspense to avoid issues with SSR
  },
};

function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  const prefix = language.toLowerCase().split('-')[0];
  const languageByPrefix: Readonly<Record<string, string>> = { en: 'en-US', es: 'es-ES' };
  document.documentElement.lang = languageByPrefix[prefix] ?? 'pt-BR';
}

// Register before init: detection and reload use the same document-language contract.
i18n.on('languageChanged', syncDocumentLanguage);
i18n.on('initialized', () => syncDocumentLanguage(i18n.language ?? 'pt-BR'));

i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(initOptions);

// Editor de workflow (o pacote do editor; o nome dele nao aparece aqui de
// proposito — `workflow-editor-i18n.test.ts` cobra a ausencia): os textos do
// pacote (3 idiomas)
// NAO sao registrados aqui — este modulo esta no chunk inicial. Quem usa o
// namespace `workflowEditor` importa `@/lib/workflow-editor-i18n`.

export default i18n;
